import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Modal, Message } from '@bijie/react-bnb';
import styled from 'styled-components';
import { validateBar, adjustBar } from '../../actions/actionsSet/margin';
import { getLabel, dyLabel } from '../../utils/i18n';
import Icon from '../Icon';
import Spinner from '../Spinner';
import { FooterComp } from './TransferModal';

const BAR_MAP = {
  '3X': '5X',
  '5X': '3X',
};
const LEVEL_MAP = {
  '3X': '1.1',
  '5X': '1.05',
};
const VALIDATELEVEL_MAP = {
  '3X': '1.25',
  '5X': '1.5',
};
const Box = styled.div`
  font-size: 14px;
  line-height: 18px;
  .title {
    padding-bottom: 24px;
    line-height: 28px;
    font-size: 22px;
    text-align: center;
  }
  .top-desc {
    padding-bottom: 32px;
  }
  .info-line {
    padding-bottom: 24px;
    p { margin-bottom: 0 }
  }
  .spinner-box {
    position: relative;
    height: 60px;
  }
  .desc {
    display: flex;
    justify-content: space-between;
    .iconfont {
      padding-right: 8px;
    }
  }
  .tip {
    margin-top: 8px;
    padding-top: 24px;
    border-top: 1px solid #eaecef;
    list-style: none;
    li {
      padding-top: 8px;
    }
  }
  .btn {
    color: #F0B90B;
    cursor: pointer;
    text-decoration: underline;
    &.level {
      padding-left: 16px;
    }
  }
  .bnc-btn {
    margin-top: -8px;
    box-shadow: none;
  }
  .primary { color: #F0B90B }
  .gray { color: #848e9c }
`;


const RowLine = ({
  i18n, row, curBar, trigerRepay, trigerTransfer,
}) => {
  const { item, pass, msg } = row;
  return (
    <div className="info-line">
      {
        pass ? (
          <div className="desc">
            <span><Icon type="check" color="#00C087" size={14} />{dyLabel(`exchange-bar-${item}`, i18n, {
              validateBar: VALIDATELEVEL_MAP[curBar],
            })}
            </span>
          </div>
        ) : (
          <>
            <div className="desc">
              <span><Icon type="error" color="#f23051" size={14} />{dyLabel(`exchange-bar-${item}`, i18n, {
                validateBar: VALIDATELEVEL_MAP[curBar],
              })}
              </span>
              {
                item !== 'MarginAccountValidation'
                && (
                <span>
                  <a className="btn" onClick={trigerRepay}>{getLabel('exchange-borrowRepayModal-repay', i18n)}</a>
                  {
                  item === 'MaxMarginLevelValidation'
                  && <a className="btn level" onClick={trigerTransfer}>{getLabel('exchange-TransferModal-Transfer', i18n)}</a>
                }
                </span>
                )
              }
            </div>
            <p className="gray">{dyLabel(`exchange-bar-${item}tip`, i18n, { msg })}</p>
          </>
        )
      }
    </div>
  );
};
RowLine.propTypes = {
  row: PropTypes.object,
  i18n: PropTypes.object,
  curBar: PropTypes.string,
  trigerRepay: PropTypes.func,
  trigerTransfer: PropTypes.func,
};

const AdjustLevelPanel = (props) => {
  const {
    i18n, closeSelf, curBar = '3X', trigerTransfer, trigerRepay,
  } = props;

  const [{ allPass, rows }, setValidates] = useState({
    allPass: false,
    rows: [],
  });
  const [fetching, setFetching] = useState(false);
  // filter rows when targetbar is 3x
  const filterRows = useMemo(() => {
    const first = rows[0];
    if (first && first.item === 'MarginAccountValidation') {
      // move item=MarginAccountValidation to the last of row
      rows.shift();
      rows.push(first);
    }
    if (curBar === '5X') return rows.filter(row => row.item !== 'MaxBorrowValidation' && row.item !== 'MarginAccountValidation');
    return rows;
  }, [rows, curBar]);

  const handleAdjust = useCallback(async () => {
    setFetching(true);
    const params = {
      bar: curBar === '3X' ? 1 : 0,
    };
    const [rs, code] = await adjustBar(params);
    if (rs) {
      Message.success(getLabel('exchange-lending-SUCCESS', i18n));
    } else {
      Message.fail(getLabel(`exchange-code-${code}`, i18n));
    }

    closeSelf(rs);
  }, [curBar]);
  const handleTransfer = useCallback(() => {
    closeSelf();
    trigerTransfer();
  }, []);
  const handleRepay = useCallback(() => {
    closeSelf();
    trigerRepay();
  }, []);

  useEffect(() => {
    const params = {
      bar: curBar === '3X' ? 1 : 0,
    };
    validateBar(params).then(rs => {
      setValidates(rs);
    });
  }, [curBar]);
  const targetBar = BAR_MAP[curBar];

  return (
    <Box>
      <div className="title">{dyLabel('exchange-bar-title', i18n, { targetBar })}</div>
      <section>
        <div className="top-desc">{dyLabel('exchange-bar-errortitle', i18n, { curBar, targetBar })}</div>
        {filterRows.length === 0 && <div className="spinner-box"><Spinner /></div>}
        {
          filterRows.map((row, idx) => (
            <RowLine
              key={idx}
              row={row}
              i18n={i18n}
              curBar={curBar}
              trigerRepay={handleRepay}
              trigerTransfer={handleTransfer}
            />
          ))
        }
        <ul className="tip gray">
          {targetBar === '5X' && <li>{getLabel('exchange-bar-bt1', i18n)}</li>}
          <li
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: dyLabel('exchange-bar-bt2', i18n, {
                level: LEVEL_MAP[targetBar],
              }),
            }}
          />
        </ul>
      </section>
      <FooterComp
        isTransfer={true}
        disable={!allPass}
        onOk={handleAdjust}
        isLoading={fetching}
        onOkText={getLabel('exchange-common-confirm', i18n)}
        loadingText={getLabel('exchange-common-processing', i18n)}
      />
    </Box>
  );
};
AdjustLevelPanel.propTypes = {
  i18n: PropTypes.object,
  curBar: PropTypes.string,
  closeSelf: PropTypes.func,
  trigerRepay: PropTypes.func,
  trigerTransfer: PropTypes.func,
};

const AdjustLevelModal = () => <></>;

AdjustLevelModal.create = params => {
  const close = Modal.create({
    title: null,
    footer: null,
    content: (
      <AdjustLevelPanel
        {...params}
        // eslint-disable-next-line no-use-before-define
        closeSelf={closeSelf}
      />
    ),
  });
  function closeSelf(reload) {
    close && close();
    params.callback(reload, reload);
  }
};

export default AdjustLevelModal;
